import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Helmet } from "react-helmet-async";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, Calendar, User, Clock, ChevronRight, BookOpen } from "lucide-react";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import DOMPurify from "dompurify";

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  content: string;
  excerpt: string | null;
  featured_image: string | null;
  category: string | null;
  tags: string[];
  author: string | null;
  published_at: string | null;
  created_at: string;
}

export default function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  // Fetch the blog post
  const { data: post, isLoading, error } = useQuery({
    queryKey: ["blog-post", slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blog_posts")
        .select("*")
        .eq("slug", slug)
        .eq("is_published", true)
        .single();
      
      if (error) throw error;
      return data as BlogPost;
    },
    enabled: !!slug,
  });

  // Fetch related posts
  const { data: relatedPosts = [] } = useQuery({
    queryKey: ["blog-related", post?.category, post?.id],
    queryFn: async () => {
      if (!post) return [];
      
      const { data, error } = await supabase
        .from("blog_posts")
        .select("id, title, slug, excerpt, featured_image, published_at")
        .eq("is_published", true)
        .neq("id", post.id)
        .order("published_at", { ascending: false })
        .limit(3);
      
      if (error) throw error;
      return data;
    },
    enabled: !!post,
  });

  // Sanitize HTML content
  const sanitizedContent = post?.content 
    ? DOMPurify.sanitize(post.content, {
        ADD_TAGS: ['iframe', 'details', 'summary', 'span'],
        ADD_ATTR: [
          'allow', 'allowfullscreen', 'frameborder', 'scrolling', 'target',
          'contenteditable', 'draggable', 'open',
          'data-h2-heading', 'data-h3-heading', 'class', 'style'
        ],
      })
    : '';

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <BookOpen className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h1 className="text-2xl font-bold mb-2">Artikel nicht gefunden</h1>
            <p className="text-muted-foreground mb-4">
              Dieser Artikel existiert nicht oder wurde entfernt.
            </p>
            <Button onClick={() => navigate("/blog")}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Zurück zum Blog
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{post.title} | MusiKonzept Blog</title>
        <meta
          name="description"
          content={
            post.excerpt && post.excerpt.length >= 50
              ? post.excerpt
              : `${post.title} – Praxistipps und Fachwissen rund um Musikpädagogik im MusiKonzept Blog.`
          }
        />
        <link rel="canonical" href={`https://www.musikonzept.de/blog/${post.slug}`} />
        <meta property="og:title" content={`${post.title} | MusiKonzept Blog`} />
        <meta
          property="og:description"
          content={
            post.excerpt && post.excerpt.length >= 50
              ? post.excerpt
              : `${post.title} – Praxistipps und Fachwissen rund um Musikpädagogik im MusiKonzept Blog.`
          }
        />
        <meta property="og:url" content={`https://www.musikonzept.de/blog/${post.slug}`} />
        <meta property="og:type" content="article" />
        {post.featured_image && <meta property="og:image" content={post.featured_image} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${post.title} | MusiKonzept Blog`} />
        <meta
          name="twitter:description"
          content={
            post.excerpt && post.excerpt.length >= 50
              ? post.excerpt
              : `${post.title} – Praxistipps und Fachwissen rund um Musikpädagogik im MusiKonzept Blog.`
          }
        />
        {post.featured_image && <meta name="twitter:image" content={post.featured_image} />}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: post.title,
          description: post.excerpt ?? undefined,
          image: post.featured_image ?? undefined,
          author: post.author ? { "@type": "Person", name: post.author } : undefined,
          datePublished: post.published_at ?? post.created_at,
          publisher: {
            "@type": "Organization",
            name: "MusiKonzept",
            logo: { "@type": "ImageObject", url: "https://www.musikonzept.de/musikonzept-logo.png" },
          },
          mainEntityOfPage: `https://www.musikonzept.de/blog/${post.slug}`,
        })}</script>
      </Helmet>

      <div className="min-h-screen flex flex-col">
        <Header />
        
        <main className="flex-1">
          {/* Hero with Featured Image */}
          {post.featured_image && (
            <div className="relative h-[40vh] min-h-[300px] max-h-[500px] overflow-hidden">
              <img 
                src={post.featured_image} 
                alt={post.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
            </div>
          )}

          {/* Content */}
          <article className="container mx-auto px-4 py-8 max-w-4xl">
            {/* Back Button */}
            <Button 
              variant="ghost" 
              className="mb-6"
              onClick={() => navigate("/blog")}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Zurück zum Blog
            </Button>

            {/* Title & Meta */}
            <header className="mb-8">
              {post.category && (
                <Badge variant="secondary" className="mb-4">
                  {post.category}
                </Badge>
              )}
              <h1 className="text-4xl font-bold mb-4">{post.title}</h1>
              
              <div className="flex flex-wrap items-center gap-4 text-muted-foreground">
                {post.author && (
                  <span className="flex items-center gap-1.5">
                    <User className="w-4 h-4" />
                    {post.author}
                  </span>
                )}
                <span className="flex items-center gap-1.5">
                  <Calendar className="w-4 h-4" />
                  {format(new Date(post.published_at || post.created_at), "d. MMMM yyyy", { locale: de })}
                </span>
              </div>
            </header>

            {/* Article Content */}
            <div 
              className="blog-content prose prose-lg dark:prose-invert max-w-none mb-12"
              dangerouslySetInnerHTML={{ __html: sanitizedContent }}
            />

            {/* Tags */}
            {post.tags && post.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-12 pt-6 border-t">
                {post.tags.map((tag) => (
                  <Badge key={tag} variant="outline">
                    #{tag}
                  </Badge>
                ))}
              </div>
            )}

            {/* Related Posts */}
            {relatedPosts.length > 0 && (
              <section className="border-t pt-12">
                <h2 className="text-2xl font-bold mb-6">Weitere Artikel</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {relatedPosts.map((related) => (
                    <Card 
                      key={related.id}
                      className="overflow-hidden cursor-pointer hover:shadow-lg transition-all group"
                      onClick={() => navigate(`/blog/${related.slug}`)}
                    >
                      {related.featured_image && (
                        <div className="aspect-video overflow-hidden">
                          <img 
                            src={related.featured_image} 
                            alt={related.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                      )}
                      <CardContent className="p-4">
                        <h3 className="font-semibold group-hover:text-primary transition-colors line-clamp-2">
                          {related.title}
                        </h3>
                        {related.published_at && (
                          <p className="text-sm text-muted-foreground mt-2">
                            {format(new Date(related.published_at), "d. MMM yyyy", { locale: de })}
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </section>
            )}
          </article>
        </main>

        <Footer />
      </div>
    </>
  );
}
